import { useMemo, useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Lock } from "lucide-react";
import { resetPassword } from "../../shared/api/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type Rule = { id: string; label: string; test: (p: string) => boolean };

const RULES: Rule[] = [
  { id: "len", label: "Ít nhất 8 ký tự", test: (p) => p.length >= 8 },
  { id: "len12", label: "Khuyến nghị ≥ 12 ký tự", test: (p) => p.length >= 12 },
  { id: "letter", label: "Có chữ cái", test: (p) => /[A-Za-zÀ-ỹ]/.test(p) },
  { id: "digit", label: "Có chữ số", test: (p) => /\d/.test(p) },
  { id: "symbol", label: "Có ký tự đặc biệt", test: (p) => /[^A-Za-z0-9À-ỹ]/.test(p) },
];

function scoreOf(password: string): { score: 0 | 1 | 2 | 3 | 4; label: string; key: string } {
  const passed = RULES.filter((r) => r.test(password)).length;
  if (password.length === 0) return { score: 0, label: "", key: "" };
  if (passed <= 1) return { score: 1, label: "Yếu", key: "weak" };
  if (passed === 2) return { score: 2, label: "Trung bình", key: "fair" };
  if (passed === 3) return { score: 3, label: "Khá", key: "good" };
  return { score: 4, label: "Mạnh", key: "strong" };
}

const STRENGTH_BAR_COLOR: Record<string, string> = {
  weak: "bg-destructive",
  fair: "bg-amber-500",
  good: "bg-yellow-500",
  strong: "bg-emerald-500",
};

/** Auth shell shared with LoginPage (ADR-0026): centered brand header + Card. */
function AuthShell({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/40 p-4 font-sans">
      <div className="w-full max-w-md">
        <div className="mb-6 text-center">
          <div className="inline-flex items-center gap-2 text-3xl font-extrabold tracking-tight text-primary">
            <span aria-hidden="true">✚</span> Addy HIS
          </div>
          <p className="mt-1 text-sm text-muted-foreground">Hệ thống quản lý phòng khám</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">{title}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </CardHeader>
          <CardContent>{children}</CardContent>
        </Card>
      </div>
    </div>
  );
}

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token");

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const strength = useMemo(() => scoreOf(password), [password]);

  if (!token) {
    return (
      <AuthShell
        title="Liên kết không hợp lệ"
        description="Liên kết đặt lại mật khẩu không có token hoặc đã hết hạn."
      >
        <div className="space-y-4">
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            Vui lòng yêu cầu một liên kết mới từ trang quên mật khẩu.
          </div>
          <Button asChild className="w-full">
            <Link to="/forgot-password">Yêu cầu liên kết mới</Link>
          </Button>
        </div>
      </AuthShell>
    );
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (strength.score < 3) {
      setError("Mật khẩu chưa đủ mạnh. Vui lòng đạt ít nhất 3 quy tắc.");
      return;
    }
    if (password !== confirm) {
      setError("Hai mật khẩu không khớp.");
      return;
    }
    setSubmitting(true);
    try {
      await resetPassword(token!, password);
      setDone(true);
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Đặt lại mật khẩu thất bại.");
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <AuthShell
        title="Mật khẩu đã được cập nhật"
        description="Bạn sẽ được chuyển về trang đăng nhập trong giây lát."
      >
        <div className="space-y-4">
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
            Thiết bị này sẽ cần đăng nhập lại từ đầu, kể cả khi đã bật "Ghi nhớ".
          </div>
          <Button asChild className="w-full">
            <Link to="/login">Đăng nhập ngay</Link>
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Đặt lại mật khẩu"
      description="Chọn mật khẩu mạnh — đây là khoá bảo vệ dữ liệu phòng khám của bạn."
    >
      <form className="space-y-4" onSubmit={onSubmit} noValidate>
        <div className="space-y-2">
          <Label htmlFor="password">Mật khẩu mới</Label>
          <div className="relative">
            <Input
              id="password"
              type={showPwd ? "text" : "password"}
              autoComplete="new-password"
              autoFocus
              className="pr-14"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
            />
            <button
              type="button"
              onClick={() => setShowPwd((v) => !v)}
              aria-label={showPwd ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
              disabled={submitting}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-sm font-medium text-muted-foreground hover:text-foreground"
            >
              {showPwd ? "Ẩn" : "Hiện"}
            </button>
          </div>

          {password.length > 0 && (
            <div className="space-y-2 pt-1">
              <div className="flex gap-1">
                {[1, 2, 3, 4].map((i) => (
                  <span
                    key={i}
                    className={`h-1.5 flex-1 rounded-full ${
                      strength.score >= i ? STRENGTH_BAR_COLOR[strength.key] : "bg-muted"
                    }`}
                  />
                ))}
              </div>
              <span className="text-sm text-muted-foreground">
                Độ mạnh: <strong className="text-foreground">{strength.label}</strong>
              </span>
              <ul className="space-y-1 text-xs">
                {RULES.map((r) => (
                  <li
                    key={r.id}
                    className={
                      r.test(password)
                        ? "text-emerald-600"
                        : "text-muted-foreground"
                    }
                  >
                    {r.test(password) ? "✓" : "○"} {r.label}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirm">Xác nhận mật khẩu</Label>
          <Input
            id="confirm"
            type={showPwd ? "text" : "password"}
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            disabled={submitting}
          />
        </div>

        {error && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        <Button type="submit" className="w-full" disabled={submitting}>
          {submitting ? "Đang cập nhật…" : "Đặt mật khẩu mới"}
        </Button>

        <div className="flex items-start gap-2 rounded-md bg-muted p-3 text-xs text-muted-foreground">
          <Lock className="mt-0.5 shrink-0 text-primary" />
          <div>
            Mật khẩu sẽ được so khớp với cơ sở dữ liệu bị rò rỉ (HIBP) trước khi chấp nhận. Tránh mật
            khẩu trùng với tài khoản cá nhân khác.
          </div>
        </div>
      </form>
    </AuthShell>
  );
}
